import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MinimalTextNavigation from '../../components/portal/MinimalTextNavigation';
import EternityLogoIntro from '../../components/portal/EternityLogoIntro';
import FeedEngine from '../../components/portal/FeedEngine';
import MobileHeader from '../../components/portal/MobileHeader';
import useFeedNavigation from '../../hooks/useFeedNavigation';
import { usePublicFeeds } from '../../hooks/usePublicFeeds';
import { FEEDS as POC_FEEDS } from '../../data/feedHierarchyPOC';

/**
 * Desktop Centered-Phone Layout
 * Premium phone device in center with vertical feed inside
 * Minimal text navigation on left, atmospheric space around
 * 
 * Now with live backend data + nested feed architecture
 */
export default function DesktopPortalLayout() {
  const [showIntro, setShowIntro] = useState(true);
  
  // Fetch live published feeds from backend
  const { feeds: liveFeeds, loading: feedsLoading, error: feedsError } = usePublicFeeds();
  
  // Use live feeds if available, fallback to POC for graceful degradation
  const FEEDS = liveFeeds || POC_FEEDS;

  // Feed navigation hook
  const feedNav = useFeedNavigation('home', FEEDS);

  // Detect if desktop (≥1024px)
  const [isDesktop, setIsDesktop] = React.useState(false);

  React.useEffect(() => {
    const checkViewport = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // Handle intro completion
  const handleIntroComplete = () => {
    setShowIntro(false);
  };

  const homePosts = useMemo(() => FEEDS.HOME?.posts || [], [FEEDS]);

  // Section key of a HOME post; POC data uses ids like 'home-work' instead of sectionKey
  const sectionOfPost = (post) => post?.sectionKey || (post?.id?.startsWith('home-') ? post.id.slice(5) : null);

  // Nav item that should be highlighted: follows the post on screen, or the root post that opened a child feed
  const rootIndex = feedNav.navigationStack[0]?.activeIndex ?? 0;
  const activeSection = sectionOfPost(homePosts[feedNav.canGoBack ? rootIndex : feedNav.currentIndex]) || 'home';

  // Handle navigation from sidebar / mobile menu
  const handleNavigation = React.useCallback((navId) => {
    const targetIndex = navId === 'home'
      ? 0
      : homePosts.findIndex(p => sectionOfPost(p) === navId);

    if (targetIndex === -1) return;

    // Both are functional state updates, so they apply in order within one render
    if (feedNav.canGoBack) feedNav.resetToRoot();
    feedNav.setActiveIndex(targetIndex);
  }, [feedNav, homePosts]);

  // Mobile experience (unchanged)
  if (!isDesktop) {
    return (
      <div className="relative w-full h-screen overflow-hidden bg-[#0A0A0F]">
        {showIntro && (
          <EternityLogoIntro onComplete={handleIntroComplete} />
        )}
        
        {!showIntro && (
          <>
            {/* Mobile Header Overlay */}
            <MobileHeader 
              canGoBack={feedNav.canGoBack}
              onGoBack={feedNav.goBack}
              onNavigate={handleNavigation}
            />
            
            {/* Feed Content */}
            <div className="w-full h-full">
              <FeedEngine
                currentFeed={feedNav.currentFeed}
                currentIndex={feedNav.currentIndex}
                onNavigateNext={feedNav.navigateNext}
                onNavigatePrev={feedNav.navigatePrev}
                onSetIndex={feedNav.setActiveIndex}
                onOpenChildFeed={feedNav.openChildFeed}
                onGoBack={feedNav.goBack}
                canGoBack={feedNav.canGoBack}
                parentFeed={feedNav.parentFeed}
                hideChrome
              />
            </div>
          </>
        )}
      </div>
    );
  }

  // Desktop experience
  return (
    <div className="relative w-full h-screen overflow-hidden bg-[#0A0A0F]">
      
      {/* INTRO ANIMATION */}
      {showIntro && (
        <EternityLogoIntro onComplete={handleIntroComplete} />
      )}

      {/* MAIN DESKTOP LAYOUT */}
      {!showIntro && (
        <div className="flex w-full h-full">
          {/* LEFT: Minimal Text Navigation */}
          <div className="w-80 flex-shrink-0 flex flex-col justify-between py-12 px-8 border-r border-white/5">
            <MinimalTextNavigation onNavigate={handleNavigation} currentSection={activeSection} />
            
            {/* Feed Indicator */}
            <div className="text-xs text-white/40 uppercase tracking-widest">
              {feedNav.currentFeed?.name || 'HOME'}
              <span className="mx-2">·</span>
              {feedNav.currentIndex + 1} / {feedNav.totalPosts}
            </div>
          </div>

          {/* CENTER: Premium Phone Container */}
          <div className="flex-1 flex items-center justify-center relative">
            {/* Ambient glow */}
            <div 
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(255, 107, 53, 0.08) 0%, transparent 60%)'
              }}
            />

            {/* Phone Device */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10"
            >
              {/* Premium Phone Shell */}
              <div className="relative" style={{ width: '390px', height: '844px' }}>
                {/* Outer frame with realistic phone styling */}
                <div className="absolute inset-0 rounded-[52px] bg-gradient-to-b from-gray-900 via-black to-gray-900 shadow-2xl">
                  {/* Screen bezel */}
                  <div className="absolute inset-[14px] rounded-[42px] bg-black overflow-hidden">
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-7 bg-black rounded-b-3xl z-50" />
                    
                    {/* Screen content */}
                    <div className="absolute inset-0 bg-black overflow-hidden rounded-[38px]">
                      <FeedEngine
                        currentFeed={feedNav.currentFeed}
                        currentIndex={feedNav.currentIndex}
                        onNavigateNext={feedNav.navigateNext}
                        onNavigatePrev={feedNav.navigatePrev}
                        onSetIndex={feedNav.setActiveIndex}
                        onOpenChildFeed={feedNav.openChildFeed}
                        onGoBack={feedNav.goBack}
                        canGoBack={feedNav.canGoBack}
                        parentFeed={feedNav.parentFeed}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* RIGHT: Atmospheric controls/indicators */}
          <div className="w-80 flex-shrink-0 flex flex-col items-end justify-center py-12 px-8 gap-6">
            {/* Current feed title */}
            <div className="text-right">
              <div className="text-white/60 text-sm font-light mb-1">
                {feedNav.currentFeed?.name || 'HOME'}
              </div>
              <div className="text-white/30 text-xs">
                {feedNav.currentIndex + 1} of {feedNav.totalPosts}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
